import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { config } from "../../config";
import { useSelector } from "react-redux";

interface WebSocketState {
  connected: boolean;
  error: string | null;
}

interface UseWebSocketOptions {
  onProgress?: (data: any) => void;
  onJobProgress?: (data: any) => void;
  onJobCompleted?: (data: any) => void;
  onJobFailed?: (data: any) => void;
}

export const useWebSocket = ({
  onProgress,
  onJobProgress,
  onJobCompleted,
  onJobFailed,
}: UseWebSocketOptions) => {
  const reduxUser: any = useSelector((state: any) => state.user);
  const userId = reduxUser.id;
  const [state, setState] = useState<WebSocketState>({
    connected: false,
    error: null,
  });

  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    // Initialize socket connection
    socketRef.current = io(config.links.agentTaskManager, {
      transports: ["websocket"],
      auth: {
        userId,
      },
    });

    // Set up event listeners
    socketRef.current.on("connect", () => {
      setState((prev) => ({ ...prev, connected: true, error: null }));

      // Track connection
      socketRef.current?.emit("track-connection", { userId });
    });

    socketRef.current.on("disconnect", () => {
      setState((prev) => ({ ...prev, connected: false }));
    });

    socketRef.current.on("connect_error", (error) => {
      setState((prev) => ({ ...prev, connected: false, error: error.message }));
    });

    // Job-related events
    socketRef.current.on("progress", (data) => {
      onProgress?.(data);
    });

    socketRef.current.on("job-progress", (data) => {
      onJobProgress?.(data);
    });

    socketRef.current.on("job-completed", (data) => {
      onJobCompleted?.(data);
    });

    socketRef.current.on("job-failed", (data) => {
      onJobFailed?.(data);
    });

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [userId]);

  // Function to emit events
  const emit = (event: string, data: any) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
      return true;
    }
    return false;
  };

  return {
    ...state,
    emit,
    socket: socketRef.current,
  };
};

export default useWebSocket;
